import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configurar interceptador do axios para incluir token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setUserType(null);
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${config.apiURL}/auth/verify-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.valid) {
        setUser(response.data.user);
        setUserType(response.data.type);
        localStorage.setItem('userType', response.data.type);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        logout();
      }
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Verificar se há token salvo no localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUserType = localStorage.getItem('userType');
    
    if (token) {
      // Se há userType salvo, usar ele temporariamente
      if (savedUserType) {
        setUserType(savedUserType);
      }
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const login = async (credentials, loginType) => {
    try {
      let endpoint;
      
      switch (loginType) {
        case 'teacher':
          endpoint = `${config.apiURL}/auth/login-teacher`;
          break;
        case 'student':
          endpoint = `${config.apiURL}/auth/login-student`;
          break;
        case 'guardian':
          endpoint = `${config.apiURL}/auth/login-guardian`;
          break;
        default:
          throw new Error('Tipo de login inválido');
      }

      const response = await axios.post(endpoint, credentials);
      const { access_token, teacher, student, guardian } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', loginType);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      let userData;
      if (teacher) userData = teacher;
      else if (student) userData = student;
      else if (guardian) userData = guardian;

      setUser(userData);
      setUserType(loginType);

      return { success: true, user: userData, type: loginType };
    } catch (error) {
      console.error('Erro no login:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao fazer login' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${config.apiURL}/auth/register-teacher`, userData);
      const { access_token, teacher } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', 'teacher');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(teacher);
      setUserType('teacher');

      return { success: true, user: teacher };
    } catch (error) {
      console.error('Erro no registro:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erro ao registrar' 
      };
    }
  };

  const value = {
    user,
    userType,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 